import React, { useEffect, useState, useCallback } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Card,
  Divider,
} from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { formatShortDateTime, customTextColor } from "../../utils/const";
import { useDispatch, useSelector } from "react-redux";
import {
  getHotlistChatDetailByIds,
  sendHotlistChatMessage,
} from "../../redux/hotlist/hotListRecSlice";
import { Icon } from "@iconify/react";

const MsgDrawer = ({ show, handleClose, candidate, selectedChat }) => {
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);
  const locations = useSelector((state) => state.location) || [];
  const reqList = useSelector((state) => state.hotlistrec.chatDetail || {});
  const loading = useSelector(
    (state) => state.hotlistrec.loading.getHotlistChatDetailByIds
  );
  const profile = useSelector((state) => state.profile?.data?.profile);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const messages = reqList || [];
  const displayedMessages = isExpanded ? messages : messages.slice(-2);

  useEffect(() => {
    if (selectedChat?.user_id && candidate?.id) {
      stableDispatch(
        getHotlistChatDetailByIds({
          hotlistId: selectedChat.user_id,
          candidateId: candidate.id,
        })
      );
    }
  }, [stableDispatch, selectedChat?.user_id, candidate?.id]);

  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleSendRequest = () => {
    if (message.trim()) {
      const messageData = {
        candidate: candidate.id,
        receiver: selectedChat.user_id,
        message: message,
      };

      stableDispatch(sendHotlistChatMessage(messageData))
        .unwrap()
        .then(() => {
          stableDispatch(
            getHotlistChatDetailByIds({
              hotlistId: selectedChat.user_id,
              candidateId: candidate.id,
            })
          );
          setMessage("");
          setShowForm(false);
        })
        .catch((error) => {
          console.error("Failed to send message:", error);
        });
    }
  };

  const handleBack = () => {
    setMessage("");
    setShowForm(false);
  };
  const getPreferLocationNames = (preferLocation) => {
    return preferLocation
      ?.map(
        (id) =>
          locations?.data?.locations?.find((location) => location.id === id)
            ?.name
      ) // Map IDs to names
      ?.filter(Boolean) // Remove any undefined values if IDs don't match
      ?.join(", "); // Join names with a comma and space
  };
  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      scroll={true}
      backdrop={false}
      style={{
        border: "none",
        marginTop: "65px",
        width: "50%",
        boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.15)",
        backgroundColor: "#fff",
      }}
    >
      <Offcanvas.Body style={{ padding: 0, margin: 0 }}>
        <Box sx={{ px: "10px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <Typography
              style={{
                color: customTextColor,
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Reply for {candidate?.full_name}
            </Typography>
            <RxCross2
              style={{
                fontSize: "15px",
                cursor: "pointer",
                color: customTextColor,
              }}
              onClick={handleClose}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "70vh",
            }}
          >
            {loading === "pending" ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            ) : showForm ? (
              <Box sx={{ px: "10px" }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  variant="outlined"
                  placeholder="Type your message here..."
                  inputProps={{ maxLength: 300 }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "3px",
                    },
                  }}
                />
                <Typography
                  style={{
                    fontSize: "10px",
                    color: customTextColor,
                    mb: 0.5,
                  }}
                >
                  {message.length}/300
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSendRequest}
                    sx={{
                      backgroundColor: "#00b0f0",
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      padding: "5px 20px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0085b4",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Send Message
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      borderColor: "#00b0f0",
                      color: "#00b0f0",
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      padding: "5px 20px",
                      "&:hover": {
                        borderColor: "#0085b4",
                        color: "#0085b4",
                      },
                    }}
                  >
                    Back
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Card
                  elevation={displayedMessages.length === 0 ? 0 : 1}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mx: 1,
                    p: 1,
                    borderRadius: "3px",
                  }}
                >
                  <Box sx={{ width: "100%", padding: "0 10px" }}>
                    {displayedMessages.length === 0 ? (
                      <Typography
                        style={{
                          fontSize: "14px",
                          color: customTextColor,
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        No messages found
                      </Typography>
                    ) : (
                      displayedMessages.map((msg, index) => (
                        <Box
                          key={msg.id}
                          sx={{
                            display: "flex",
                            mb: 2,
                            borderBottom:
                              index < displayedMessages.length - 1
                                ? "1px solid #e0e0e0"
                                : "none",
                            pb: index < displayedMessages.length - 1 ? 2 : 0,
                          }}
                        >
                          <Box sx={{ mr: 2 }}>
                            <img
                              src={
                                msg.sender.profile_picture ||
                                "https://via.placeholder.com/42"
                              }
                              alt="Sender Profile"
                              style={{
                                width: "50px",
                                height: "50px",
                                marginTop: "4px",
                                backgroundColor: "#fff",
                                objectFit: "contain",
                                borderRadius: "3px",
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {msg.sender.full_name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#666",
                                    mt: -0.2,
                                  }}
                                >
                                  {msg.sender.designation} -{" "}
                                  {msg.sender.company_name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "gray",
                                    mt: -0.2,
                                  }}
                                >
                                  Ph:{" "}
                                  {msg.sender.contact?.mobile_no ||
                                    "Not provided"}{" "}
                                  Mail:{" "}
                                  {msg.sender.contact?.email || "Not provided"}
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                }}
                              >
                                {formatShortDateTime(msg.timestamp)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                bgcolor:
                                  msg?.job_title && msg.rate
                                    ? "#fafafa"
                                    : "#ffff",
                                px: msg?.job_title && msg.rate ? 2 : 0,
                                py: msg?.job_title && msg.rate ? 0.5 : 0,
                                borderRadius: "3px",

                                my: 0.5,
                              }}
                            >
                              {msg?.job_title && msg.rate && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      maxWidth: "50%",
                                    }}
                                  >
                                    <Typography>
                                      <b>Job Title:</b>{" "}
                                      {msg.job_title ? msg.job_title : "n/a"}
                                    </Typography>
                                    <Typography>
                                      <b>Job Location:</b>{" "}
                                      {getPreferLocationNames(
                                        msg?.job_location
                                      ) || "n/a"}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      maxWidth: "50%",
                                    }}
                                  >
                                    <Typography>
                                      <b>Rate:</b>{" "}
                                      {msg.rate ? `$${msg.rate} Houly` : "n/a "}
                                    </Typography>
                                    <Typography>
                                      <b>End Client:</b>{" "}
                                      {msg?.end_client
                                        ? msg?.end_client
                                        : "n/a"}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              {msg?.job_title && msg.rate && <Divider />}

                              <Box
                                sx={{
                                  mt: 1,
                                  display: "flex",
                                  justifyContent: "space-between",

                                  borderRadius: "3px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {msg.message}
                                </Typography>

                                {profile.user_id === msg.sender.user_id
                                  ? msg.is_read && (
                                      <Icon
                                        icon="charm:tick-double"
                                        style={{
                                          color: msg.is_read
                                            ? "#1e90ff"
                                            : "gray",
                                        }}
                                      />
                                    )
                                  : index === displayedMessages.length - 1 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-end",
                                        }}
                                      >
                                        <button
                                          style={{
                                            backgroundColor: "#1e90ff",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "3px",
                                            padding: "2px 6px",
                                            fontSize: "8px",
                                            cursor: "default",
                                          }}
                                        >
                                          Replied
                                        </button>
                                      </Box>
                                    )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ))
                    )}
                    {messages.length > 2 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "end",
                          mt: 2,
                          px: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => setIsExpanded(!isExpanded)}
                        >
                          {isExpanded ? "View Less" : "View More"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
                {displayedMessages.length !== 0 && (
                  <Box sx={{ padding: "0px 10px", my: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowForm(true)}
                      sx={{
                        backgroundColor: "#00b0f0",
                        textTransform: "capitalize",
                        borderRadius: "3px",
                        padding: "5px 20px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#0085b4",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Send Message
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MsgDrawer;
